import React, { useState, useEffect } from "react";
import "../styles/ManualAdjustment.css";
import apiClient from "../api/api";
import { isWeekend } from "date-fns";
import { DateTime } from "luxon";

const ManualAdjustment = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [adjustedTimes, setAdjustedTimes] = useState({
    clock_in: "",
    clock_out: "",
    break_start: "",
    break_end: "",
  });
  const [adminSettings, setAdminSettings] = useState(null);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Add Entry state
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [newEntry, setNewEntry] = useState({
    user_id: "",
    date: new Date().toISOString().split("T")[0],
    clock_in: "",
    clock_out: "",
    break_start: "",
    break_end: "",
    reason: "",
  });
  const [addEntryError, setAddEntryError] = useState(null);
  const [isSavingEntry, setIsSavingEntry] = useState(false);

  useEffect(() => {
    const fetchSettingsAndHolidays = async () => {
      try {
        const [settingsRes, holidaysRes] = await Promise.all([
          apiClient.get("/admin-settings"),
          apiClient.get("/holidays"),
        ]);
        setAdminSettings(settingsRes.data);
        setPublicHolidays(holidaysRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchSettingsAndHolidays();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        // /employees/simplified returns flat [{id, name}] — correct shape for dropdown
        const res = await apiClient.get("/employees/simplified");
        setEmployees(res.data || []);
      } catch (err) {
        console.error("Error fetching employees:", err);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(
          `/adjustments/attendance?date=${selectedDate}`
        );
        if (response.data.success) {
          setAttendanceData(response.data.data);
        } else {
          setError(response.data.error || "Failed to fetch attendance data");
          setAttendanceData([]);
        }
      } catch (err) {
        console.error("Error fetching attendance data:", err);
        setError("Failed to fetch attendance data");
        setAttendanceData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedDate]);

  const formatTime = (dateTime, fmt = "HH:mm") => {
    if (!dateTime) return "--:--";
    try {
      const londonTime = DateTime.fromJSDate(new Date(dateTime)).setZone("Europe/London");
      return londonTime.toFormat(fmt);
    } catch (err) {
      return "--:--";
    }
  };

  const formatDateTimeForInput = (dateTime) => {
    if (!dateTime) return "";
    try {
      const str = typeof dateTime === "string" ? dateTime.replace(" ", "T") : dateTime;
      let dt = DateTime.fromISO(str, { zone: "UTC" }).setZone("Europe/London");
      if (!dt.isValid) {
        dt = DateTime.fromJSDate(new Date(dateTime)).setZone("Europe/London");
      }
      return dt.isValid ? dt.toFormat("yyyy-MM-dd'T'HH:mm") : "";
    } catch (err) {
      return "";
    }
  };

  const formatMinutes = (totalMinutes) => {
    if (totalMinutes <= 0) return "0h 0m";
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const toMinutes = (timeStr) => {
    const [hours, mins] = timeStr.split(":").map(Number);
    return hours * 60 + mins;
  };

  const isPublicHoliday = (date) => {
    if (!publicHolidays.length) return false;
    const dateStr = formatTime(new Date(date), "dd-MM-yyyy");
    return publicHolidays.some((holiday) => holiday.date === dateStr);
  };

  const calculateWorkType = (minutesWorked, recordDate, userRole) => {
    if (userRole && userRole.toLowerCase() === "outsource") return "Outsource";
    if (!adminSettings) return "N/A";
    const date = new Date(recordDate);
    if (isWeekend(date) || isPublicHoliday(date)) return "Overtime";
    const fullDay = toMinutes(adminSettings.full_day_hours);
    const halfDay = toMinutes(adminSettings.half_day_hours);
    const shortDay = toMinutes(adminSettings.short_day_hours);
    if (minutesWorked >= fullDay) return "Full Day";
    if (minutesWorked >= halfDay) return "Half Day";
    if (minutesWorked >= shortDay) return "Short Day";
    return "Absent";
  };

  const filteredData = attendanceData.filter(
    (record) =>
      record.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.user_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const showSuccess = (message) => {
    setNotification(
      <div className="popup-notification success">
        <div className="popup-content">
          <span>?</span>
          <p>{message}</p>
        </div>
      </div>
    );
    setTimeout(() => setNotification(null), 3000);
  };

  const openEditPopup = (record) => {
    setSelectedRecord(record);
    setAdjustedTimes({
      clock_in:    record.clock_in    ? formatDateTimeForInput(record.clock_in)    : "",
      clock_out:   record.clock_out   ? formatDateTimeForInput(record.clock_out)   : "",
      break_start: record.break_start ? formatDateTimeForInput(record.break_start) : "",
      break_end:   record.break_end   ? formatDateTimeForInput(record.break_end)   : "",
    });
    setAdjustmentReason("");
    setShowPopup(true);
  };

  const handleTimeChange = (field, value) => {
    setAdjustedTimes((prev) => ({ ...prev, [field]: value }));
  };

  const saveAdjustments = async () => {
    if (isSaving) return;
    if (!adjustmentReason.trim()) { setError("Reason for adjustment is required"); return; }
    setIsSaving(true);
    setError(null);
    try {
      const parseToLondon = (val) =>
        val ? DateTime.fromFormat(val, "yyyy-MM-dd'T'HH:mm", { zone: "Europe/London" }).toISO() : null;

      const adjustments = {};
      if (adjustedTimes.clock_in)    adjustments.clock_in    = parseToLondon(adjustedTimes.clock_in);
      if (adjustedTimes.clock_out)   adjustments.clock_out   = parseToLondon(adjustedTimes.clock_out);
      if (adjustedTimes.break_start) adjustments.break_start = parseToLondon(adjustedTimes.break_start);
      if (adjustedTimes.break_end)   adjustments.break_end   = parseToLondon(adjustedTimes.break_end);

      const response = await apiClient.post("/adjustments/adjust", {
        attendance_id: selectedRecord.id,
        adjustments,
        reason: adjustmentReason,
      });

      if (response.data.success) {
        // Refetch current date's attendance to reflect any moved or updated records
        const refreshed = await apiClient.get(`/adjustments/attendance?date=${selectedDate}`);
        if (refreshed.data.success) {
          setAttendanceData(refreshed.data.data);
        }
        showSuccess(`Adjustment saved successfully for ${selectedRecord.user_name}`);
        setShowPopup(false);
        setSelectedRecord(null);
      } else {
        setError(response.data.error || "Failed to save adjustment");
      }
    } catch (err) {
      console.error("Error saving adjustment:", err);
      setError("Failed to save adjustment");
    } finally {
      setIsSaving(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedRecord(null);
    setError(null);
  };

  const openAddEntry = () => {
    setNewEntry({
      user_id: "",
      date: selectedDate,
      clock_in: "",
      clock_out: "",
      break_start: "",
      break_end: "",
      reason: "",
    });
    setAddEntryError(null);
    setShowAddEntry(true);
  };

  const closeAddEntry = () => {
    setShowAddEntry(false);
    setAddEntryError(null);
  };

  const handleNewEntryChange = (field, value) => {
    setNewEntry((prev) => ({ ...prev, [field]: value }));
  };

  const saveNewEntry = async () => {
    if (isSavingEntry) return;
    if (!newEntry.user_id)       { setAddEntryError("Please select an employee."); return; }
    if (!newEntry.date)          { setAddEntryError("Please select a date."); return; }
    if (!newEntry.clock_in)      { setAddEntryError("Clock In time is required."); return; }
    if (!newEntry.reason.trim()) { setAddEntryError("Reason is required."); return; }

    setIsSavingEntry(true);
    setAddEntryError(null);

    const parseToLondon = (dateStr, timeStr) => {
      if (!timeStr) return null;
      return DateTime.fromFormat(`${dateStr}T${timeStr}`, "yyyy-MM-dd'T'HH:mm", {
        zone: "Europe/London",
      }).toISO();
    };

    try {
      const payload = {
        user_id:     newEntry.user_id,
        date:        newEntry.date,
        clock_in:    parseToLondon(newEntry.date, newEntry.clock_in),
        clock_out:   newEntry.clock_out   ? parseToLondon(newEntry.date, newEntry.clock_out)   : null,
        break_start: newEntry.break_start ? parseToLondon(newEntry.date, newEntry.break_start) : null,
        break_end:   newEntry.break_end   ? parseToLondon(newEntry.date, newEntry.break_end)   : null,
        reason:      newEntry.reason,
      };

      const response = await apiClient.post("/adjustments/manual-entry", payload);

      if (response.data.success) {
        if (selectedDate === newEntry.date) {
          const refreshed = await apiClient.get(`/adjustments/attendance?date=${selectedDate}`);
          if (refreshed.data.success) setAttendanceData(refreshed.data.data);
        } else {
          // Switch page view to the new entry's date so the newly added record displays immediately
          setSelectedDate(newEntry.date);
        }
        const empName =
          employees.find((e) => String(e.id) === String(newEntry.user_id))?.name || "Employee";
        showSuccess(`Manual entry added successfully for ${empName} on ${newEntry.date}`);
        setShowAddEntry(false);
      } else {
        setAddEntryError(response.data.error || "Failed to create entry.");
      }
    } catch (err) {
      console.error("Error creating manual entry:", err);
      const serverMsg = err.response?.data?.error || err.response?.data?.message;
      if (serverMsg) {
        setAddEntryError(serverMsg);
      } else if (err.response?.status === 404) {
        setAddEntryError("Backend endpoint not found (404). Please upload server files and restart the Node.js server.");
      } else {
        setAddEntryError(err.message || "Failed to create entry. Please try again.");
      }
    } finally {
      setIsSavingEntry(false);
    }
  };

  return (
    <div className="manual-adjustment-container">
      <h1>Manual Attendance Adjustment</h1>

      <div className="controls">
        <div className="date-selector">
          <label>Date: </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
          <span style={{ marginLeft: "8px", fontWeight: "600", color: "#475569", fontSize: "13px" }}>
            ({DateTime.fromISO(selectedDate).toFormat("dd/MM/yyyy")})
          </span>
        </div>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="add-entry-btn" onClick={openAddEntry}>
          + Add Entry
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="attendance-list">
        {loading ? (
          <div className="loading">Loading attendance data...</div>
        ) : filteredData.length === 0 ? (
          <div className="no-data">
            {searchTerm ? "No matching results found" : "No attendance data for selected date"}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Clock In</th>
                <th>Clock Out</th>
                <th>Break In</th>
                <th>Break Out</th>
                <th>Work Type</th>
                <th>Total Time</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((record) => {
                const toLD = (iso) =>
                  iso ? DateTime.fromISO(iso, { zone: "UTC" }).setZone("Europe/London") : null;
                const ci = toLD(record.clock_in);
                const co = toLD(record.clock_out);
                const bs = toLD(record.break_start);
                const be = toLD(record.break_end);
                return (
                  <tr key={record.id}>
                    <td>{record.serial_number}</td>
                    <td>{record.user_name}</td>
                    <td>{ci ? ci.toFormat("HH:mm") : "--:--"}</td>
                    <td>{co ? co.toFormat("HH:mm") : "--:--"}</td>
                    <td>{bs ? bs.toFormat("HH:mm") : "--:--"}</td>
                    <td>{be ? be.toFormat("HH:mm") : "--:--"}</td>
                    <td>{calculateWorkType(record.total_work_minutes, record.date, record.user_role)}</td>
                    <td>{formatMinutes(record.total_work_minutes)}</td>
                    <td>
                      <button className="edit-btn" onClick={() => openEditPopup(record)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showPopup && selectedRecord && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h2>Adjust Times for {selectedRecord.user_name}</h2>
            <p style={{ fontSize: "14px", color: "#475569", marginBottom: "12px" }}>
              Record Date: <strong>{DateTime.fromISO(selectedRecord.date).toFormat("dd/MM/yyyy")}</strong>
            </p>
            <div className="time-adjustment-grid">
              {["clock_in", "clock_out", "break_start", "break_end"].map((field) => (
                <div className="time-field" key={field}>
                  <label>
                    {field.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}:
                  </label>
                  <input
                    type="datetime-local"
                    value={adjustedTimes[field]}
                    onChange={(e) => handleTimeChange(field, e.target.value)}
                  />
                  <div className="current-value">Current: {formatTime(selectedRecord[field])}</div>
                </div>
              ))}
            </div>
            <div className="reason-field">
              <label>Reason for Adjustment (required):</label>
              <textarea
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
                placeholder="Explain why these times are being adjusted"
                rows="3"
              />
            </div>
            <div className="popup-buttons">
              <button onClick={closePopup} className="cancel-btn">Cancel</button>
              <button
                onClick={saveAdjustments}
                className="save-btn"
                disabled={isSaving || !adjustmentReason.trim()}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddEntry && (
        <div className="popup-overlay">
          <div className="popup-box popup-box--wide">
            <h2>Add Manual Entry</h2>

            {addEntryError && (
              <div className="error-message" style={{ marginBottom: "15px" }}>
                {addEntryError}
              </div>
            )}

            <div className="add-entry-grid">
              <div className="add-entry-field add-entry-field--full">
                <label>Employee <span className="required">*</span></label>
                <select
                  value={newEntry.user_id}
                  onChange={(e) => handleNewEntryChange("user_id", e.target.value)}
                  className="add-entry-select"
                >
                  <option value="">-- Select Employee --</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="add-entry-field add-entry-field--full">
                <label>Date <span className="required">*</span></label>
                <input
                  type="date"
                  value={newEntry.date}
                  onChange={(e) => handleNewEntryChange("date", e.target.value)}
                  className="add-entry-input"
                />
              </div>

              {[
                { field: "clock_in",    label: "Clock In",  required: true  },
                { field: "clock_out",   label: "Clock Out", required: false },
                { field: "break_start", label: "Break In",  required: false },
                { field: "break_end",   label: "Break Out", required: false },
              ].map(({ field, label, required }) => (
                <div className="add-entry-field" key={field}>
                  <label>
                    {label} {required && <span className="required">*</span>}
                  </label>
                  <input
                    type="time"
                    value={newEntry[field]}
                    onChange={(e) => handleNewEntryChange(field, e.target.value)}
                    className="add-entry-input"
                  />
                </div>
              ))}

              <div className="add-entry-field add-entry-field--full">
                <label>Reason <span className="required">*</span></label>
                <textarea
                  value={newEntry.reason}
                  onChange={(e) => handleNewEntryChange("reason", e.target.value)}
                  placeholder="Reason for adding this manual entry"
                  rows="3"
                  className="add-entry-textarea"
                />
              </div>
            </div>

            <div className="popup-buttons">
              <button onClick={closeAddEntry} className="cancel-btn">Cancel</button>
              <button
                onClick={saveNewEntry}
                className="save-btn"
                disabled={isSavingEntry}
              >
                {isSavingEntry ? "Adding..." : "Add Entry"}
              </button>
            </div>
          </div>
        </div>
      )}

      {notification}
    </div>
  );
};

export default ManualAdjustment;
